import React, { useContext, useState } from "react";
import { JobsContext } from "./JobsContext";

const emptyJob = {
  title: "",
  location: "",
  category: "",
  type: "",
  experience: "",
  summary: "",
  responsibilities: [""],
  requirements: [""],
};

export default function JobsAdmin() {
  const { jobs, addJob, updateJob, deleteJob, loading } =
    useContext(JobsContext);

  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [errors, setErrors] = useState({});

  const startAdd = () => {
    setFormData(emptyJob);
    setIsEditing(false);
  };

  const startEdit = (job) => {
    setFormData(job);
    setIsEditing(true);
  };

  const cancel = () => {
    setFormData(null);
    setIsEditing(false);
  };

  const save = async () => {
    const newErrors = {};

    if (!formData.title?.trim()) newErrors.title = "Job title is required";
    if (!formData.location?.trim()) newErrors.location = "Location is required";
    if (!formData.category?.trim()) newErrors.category = "Category is required";
    if (!formData.type?.trim()) newErrors.type = "Job type is required";
    if (!formData.experience?.trim())
      newErrors.experience = "Experience is required";
    if (!formData.summary?.trim()) newErrors.summary = "Summary is required";

    if (
      !formData.responsibilities ||
      formData.responsibilities.filter((r) => r.trim() !== "").length === 0
    ) {
      newErrors.responsibilities = "At least one responsibility is required";
    }

    if (
      !formData.requirements ||
      formData.requirements.filter((r) => r.trim() !== "").length === 0
    ) {
      newErrors.requirements = "At least one requirement is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    isEditing ? await updateJob(formData) : await addJob(formData);
    cancel();
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-brand-dark">Loading jobs...</div>
    );
  }
  console.log("🔥 Jobs in Admin:", jobs);

  return (
    <div className="min-h-screen bg-[var(--bg-1)] p-6 md:p-10">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
        <div>
          <h1 className="text-4xl font-serif text-[var(--brand-dark)] mb-1">
            Careers Management
          </h1>
          <p className="text-sm text-gray-500">
            Manage all job openings, roles and opportunities
          </p>
        </div>

        <button
          onClick={startAdd}
          className="mt-4 md:mt-0 bg-[var(--brand-gold)] text-white px-6 py-3 rounded-lg font-medium shadow hover:shadow-lg transition-all duration-200"
        >
          + Add New Job
        </button>
      </div>

      {/* ================= FORM CARD ================= */}
      {formData && (
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-10 border border-[var(--bg-3)]">
          <h2 className="text-xl font-semibold text-[var(--brand-dark)] mb-6">
            {isEditing ? "Edit Job" : "Add New Job"}
          </h2>

          <div className="grid md:grid-cols-2 gap-5 mb-6">
            {/* Job Title */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                placeholder="e.g. Site Engineer"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                placeholder="e.g. Visakhapatnam"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
              />
              {errors.location && (
                <p className="text-red-600 text-sm mt-1">{errors.location}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
              >
                <option value="">Select Category</option>
                <option value="Engineering">Engineering</option>
                <option value="Management">Management</option>
                <option value="Sales">Sales</option>
                <option value="HR">HR</option>
                <option value="Creative">Creative</option>
              </select>
              {errors.category && (
                <p className="text-red-600 text-sm mt-1">{errors.category}</p>
              )}
            </div>

            {/* Job Type */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Job Type <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                placeholder="Full Time / Part Time"
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
              />
              {errors.type && (
                <p className="text-red-600 text-sm mt-1">{errors.type}</p>
              )}
            </div>

            {/* Experience */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Experience <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                placeholder="e.g. 2-4 Years"
                value={formData.experience}
                onChange={(e) =>
                  setFormData({ ...formData, experience: e.target.value })
                }
              />
              {errors.experience && (
                <p className="text-red-600 text-sm mt-1">{errors.experience}</p>
              )}
            </div>
          </div>

          {/* Job Summary */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--brand-dark)]">
              Job Summary <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
              placeholder="Short summary about this role..."
              value={formData.summary}
              onChange={(e) =>
                setFormData({ ...formData, summary: e.target.value })
              }
            />
            {errors.summary && (
              <p className="text-red-600 text-sm mt-1">{errors.summary}</p>
            )}
          </div>

          {/* Responsibilities */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--brand-dark)] mb-2 block">
              Responsibilities <span className="text-red-500">*</span>
            </label>

            {formData.responsibilities.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  className="w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                  placeholder={`Responsibility ${index + 1}`}
                  value={item}
                  onChange={(e) => {
                    const updated = [...formData.responsibilities];
                    updated[index] = e.target.value;
                    setFormData({ ...formData, responsibilities: updated });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...formData.responsibilities];
                    updated.splice(index, 1);
                    setFormData({ ...formData, responsibilities: updated });
                  }}
                  className="px-3 rounded bg-red-500 text-white"
                >
                  ×
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  responsibilities: [...formData.responsibilities, ""],
                })
              }
              className="text-sm text-[var(--brand-gold)] mt-1"
            >
              + Add Responsibility
            </button>
            {errors.responsibilities && (
              <p className="text-red-600 text-sm mt-1">
                {errors.responsibilities}
              </p>
            )}
          </div>
          {/* Requirements */}
          <div className="mb-6">
            <label className="text-sm font-medium text-[var(--brand-dark)] mb-2 block">
              Requirements{" "}
              {errors.responsibilities && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.responsibilities}
                </p>
              )}
            </label>

            {formData.requirements.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  className="w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                  placeholder={`Requirement ${index + 1}`}
                  value={item}
                  onChange={(e) => {
                    const updated = [...formData.requirements];
                    updated[index] = e.target.value;
                    setFormData({ ...formData, requirements: updated });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...formData.requirements];
                    updated.splice(index, 1);
                    setFormData({ ...formData, requirements: updated });
                  }}
                  className="px-3 rounded bg-red-500 text-white"
                >
                  ×
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  requirements: [...formData.requirements, ""],
                })
              }
              className="text-sm text-[var(--brand-gold)] mt-1"
            >
              + Add Requirement
            </button>
            {errors.requirements && (
              <p className="text-red-600 text-sm mt-1">{errors.requirements}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={save}
              className="bg-[var(--brand-dark)] text-white px-6 py-2 rounded-md hover:opacity-90 transition"
            >
              Save Job
            </button>
            <button
              onClick={cancel}
              className="bg-gray-300 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ================= TABLE CARD ================= */}
      <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[var(--bg-3)]">
        <h2 className="text-xl font-semibold text-[var(--brand-dark)] mb-6">
          All Jobs
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-3)] text-[var(--brand-dark)]">
                <th className="text-left p-3 font-semibold">Title</th>
                <th className="text-left p-3 font-semibold">Location</th>
                <th className="text-left p-3 font-semibold">Category</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.id}
                  className="border-b last:border-none hover:bg-[var(--bg-2)] transition"
                >
                  <td className="p-3">{j.title}</td>
                  <td className="p-3">{j.location}</td>
                  <td className="p-3">
                    <span className="px-3 py-1 rounded-full text-xs bg-[var(--bg-3)] text-[var(--brand-dark)]">
                      {j.category}
                    </span>
                  </td>
                  <td className="p-3 text-right space-x-2">
                    <button
                      onClick={() => startEdit(j)}
                      className="text-[var(--brand-gold)] font-medium hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setJobToDelete(j)}
                      className="text-red-600 font-medium hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {jobs.length === 0 && (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-gray-500">
                    No jobs added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {jobToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-lg">
            <h3 className="text-lg font-semibold text-brand-dark mb-3">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <b>{jobToDelete.title}</b>? This
              action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setJobToDelete(null)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await deleteJob(jobToDelete.id);
                  setJobToDelete(null);
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
